import React, {useCallback, useEffect, useId, useRef, useState} from 'react';
import {Editor, ExposedEditorData} from './Editor/Editor';
import {noop} from '@priority-software/priority-style-react';
import {formsStore} from '../../../../stores/Form/FormsStore';
import {Form} from '../../../../stores/Form/Form';
import {eventPrevented} from '../../../../utils/eventPrevented';
import {runSiblingSubform} from '../../utils/grid-methods';
import {subFormNavDirections} from '../../consts/grid';
import {keys} from '../../../../constants/KeyCodes';
import {checkAndSaveHtml} from '../../actions/form';
import {observer} from 'mobx-react';
import {
    MAX_AUTOSAVE_ATTEMPTS,
    MILLISECONDS_PER_SECOND,
    SECONDS_PER_MINUTE
} from './Editor/constants';
import {changeLockerStatus} from '../ScreenLocker/actions';
import {apiLocker, UpdateStatusTypes} from '../../../../stores/Form/API_Locker';
import styles from './styles.module.scss';
import {getUsername} from '../../../../_shared/GWTBridge/invoke/genPage';
import {handleHTMLLockedState} from '../../actions/messages';
import {removeStyleTags} from './utils/sanitizeHtml';

type Props = {
    form: Form;
    readonly?: boolean;
    isApiLock: () => boolean;
    enterSubform: (subFormName: string, level: number, isHTMLForm: boolean) => Promise<void>;
};

export const HTMLForm = observer(({form, readonly, isApiLock, enterSubform}: Props) => {
    const {level, HTMLContent} = form;
    const parentLevel = form.parentForm?.level;
    const isActive = formsStore.getActiveForm() === form;
    const formID = useId();

    const editorRef = useRef<ExposedEditorData>();

    // Autosave state management
    const [autosaveCounter, setAutosaveCounter] = useState(0);
    const [isDirty, setIsDirty] = useState(false);
    const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const saveDisabled =
        isApiLock() ||
        apiLocker.getDataBySession(form.session)?.status === UpdateStatusTypes.updating ||
        !isDirty;

    // Get autosave interval from system constants (in minutes)
    const getAutosaveInterval = useCallback((): number | null => {
        const autosaveText = formsStore.sysConstants?.autosavetext?.Int;
        if (autosaveText) {
            return Number(autosaveText) * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND; // Convert minutes to milliseconds
        }
        return null; // No autosave if constant is not set or invalid
    }, []);

    // Autosave function
    const performAutosave = useCallback(async () => {
        if (!isActive) return;

        if (isDirty) {
            await checkAndSaveHtml(form);
            setIsDirty(false); // Reset dirty state after successful save
        }

        // Always increment counter regardless of not-dirty skip
        setAutosaveCounter(prev => prev + 1);
    }, [form, isActive, isDirty]);

    // Exit to parent form after 3 failed attempts
    const exitToParentForm = useCallback(async () => {
        if (parentLevel !== undefined) {
            await formsStore.currentScreen.handleFormLevel(parentLevel);
        }
    }, [parentLevel]);

    const onSave = useCallback(async () => {
        try {
            changeLockerStatus(form.session, UpdateStatusTypes.updating, true);
            const isOk = await checkAndSaveHtml(form);
            changeLockerStatus(
                form.session,
                !!isOk ? UpdateStatusTypes.saved : UpdateStatusTypes.none,
                true
            );
            setIsDirty(!isOk);
        } catch (e) {
            changeLockerStatus(form.session, UpdateStatusTypes.none, true);
            setIsDirty(false); // Reset dirty state after manual save
        }
    }, [form]);

    const preserveHTMLState = useCallback(
        (html: string) => {
            // save HTML state when user switch between the screens
            form.setHTMLContent(html);
        },
        [form]
    );

    const onFocus = useCallback(async () => {
        const editorAPI = editorRef.current?.editor.current;
        if (editorAPI) {
            await formsStore.currentScreen.onFieldFocusChange(
                {
                    level,
                    newCellIndex: undefined,
                    newRowIndex: undefined
                },
                noop
            );
        }
    }, [level]);

    // Autosave timer management
    useEffect(() => {
        if (!isActive || readonly) {
            // Clear timer if form is not active or readonly
            if (autosaveTimerRef.current) {
                clearInterval(autosaveTimerRef.current);
                autosaveTimerRef.current = null;
            }
            // Reset counter when exiting the subform
            setAutosaveCounter(0);
            return noop;
        } else {
            const interval = getAutosaveInterval();
            if (interval) {
                // Set up autosave timer
                autosaveTimerRef.current = setInterval(performAutosave, interval);

                return () => {
                    if (autosaveTimerRef.current) {
                        clearInterval(autosaveTimerRef.current);
                        autosaveTimerRef.current = null;
                    }
                };
            }
            return noop;
        }
    }, [isActive, readonly, getAutosaveInterval, performAutosave]);

    // Handle autosave counter changes - exit after 3 saved attempts
    useEffect(() => {
        if (autosaveCounter >= MAX_AUTOSAVE_ATTEMPTS) {
            exitToParentForm();
        }
    }, [autosaveCounter, exitToParentForm]);

    // Monitor editor for dirty state changes
    useEffect(() => {
        if (isActive && form._htmlEditorInstance && !isDirty) {
            const removeUpdateListener = form._htmlEditorInstance.registerUpdateListener(
                ({dirtyElements, dirtyLeaves}) => {
                    if (dirtyElements.size > 0 || dirtyLeaves.size > 0) {
                        setIsDirty(true);
                        setAutosaveCounter(0); // Reset counter when content changes
                    }
                }
            );
            return () => removeUpdateListener();
        }
        return noop;
    }, [isActive, form._htmlEditorInstance, isDirty]);

    useEffect(() => {
        if (isActive) {
            const editorInstance = editorRef.current?.editor?.current;
            if (editorRef.current) {
                form.setEditorInstance(editorInstance);
            }

            const isSubForm = !!form.parentForm;

            const onKeyDown = async (e: KeyboardEvent) => {
                if (!isApiLock() && !formsStore.currentScreen?.isNavigationBlocked) {
                    if (e.code === keys.ESC) {
                        e.preventDefault();
                        await formsStore.currentScreen.handleFormLevel(parentLevel);
                    } else if (e.code === keys.ARROW_LEFT) {
                        if (e.altKey && isSubForm) {
                            eventPrevented(e, true);
                            await runSiblingSubform(subFormNavDirections.left, form);
                        }
                    } else if (e.code === keys.ARROW_RIGHT) {
                        if (e.altKey && isSubForm) {
                            eventPrevented(e, true);
                            await runSiblingSubform(subFormNavDirections.right, form);
                        }
                    }
                }
            };

            window.addEventListener('keydown', onKeyDown);
            return () => window.removeEventListener('keydown', onKeyDown);
        }
        // clear editor ref in the form store;
        form.setEditorInstance(null);
        return noop;
    }, [parentLevel, isActive, form, isApiLock, readonly]);

    const handleClick = async () => {
        if (formsStore.currentScreen?.activeLevel === form.level && form.lockBy === getUsername()) {
            const allowLevelChange = await handleHTMLLockedState(form, false);
            const parentLevel = form?.parentForm?.level;
            formsStore.currentScreen.setActiveLevel(
                allowLevelChange ? parentLevel + 1 : parentLevel
            );
            return;
        }

        await enterSubform(form.ename, form.parentForm.level, true);
    };

    const parentInSearchMode = !!form?.parentForm?.searchModeLevel;

    const content =
        readonly || !isActive ? (
            <div
                id={formID}
                onClick={handleClick}
                className={styles.readOnlyContent}
                dangerouslySetInnerHTML={{
                    __html: parentInSearchMode ? '' : removeStyleTags(HTMLContent)
                }}
            />
        ) : (
            <Editor
                ref={editorRef}
                namespace="HTML-form-editor"
                value={HTMLContent}
                isReadonly={false}
                onSave={onSave}
                onFocus={onFocus}
                autofocus={false}
                onUnmount={preserveHTMLState}
                formID={formID}
                session={form.session}
                saveDisabled={saveDisabled}
            />
        );

    return <div className={styles.component}>{content}</div>;
});
